import React, { useState } from 'react';
import BossDialog from '../../components/BossDialog';
import { EditPageHeader } from '../../components/EditPageHeader';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useTranslation } from '../../locale/useTranslation';
import * as API from "../../framework/API/api";
import { utility } from '../../framework/utility/utilityProvider';
import { ENTITYNAME, LOGEVENT, MODULE, TOAST_TYPES } from '../../framework/constant/constant';
import TooltipInfoCard from '../../components/TooltipInfoCard';
import ToastNotification from '../../components/ToastNotification';

const AD_INSERTION_MODE = {
    STITCHED_ONLY: 'STITCHED_ONLY',
    DYNAMIC_AD_INSERTION: 'DYNAMIC_AD_INSERTION',
    CLIENT_SIDE_AD_INSERTION: 'CLIENT_SIDE_AD_INSERTION',
};

const AVAIL_SUPPRESSION_MODE = {
    OFF: 'OFF',
    ON: 'ON'
};

const AVAIL_SUPPRESSION_FILL_POLICY = {
    FULL_AVAIL_ONLY: 'FULL_AVAIL_ONLY',
    PARTIAL_AVAIL: 'PARTIAL_AVAIL',
    NO_SUPPRESSION: 'NO_SUPPRESSION',
};

const DaiAdInsertionForm = ({ item = {}, cancelEdit, refresh, setData }) => {

    const lang = useTranslation();
    const blankDataItem = {
        _id: item?._id,
        SID: item?.SID,
        Name: item?.copy ? "Copy of " + item?.Name : item?.Name ?? "",
        EnableAdMarkerPassthrough: item?.EnableAdMarkerPassthrough ?? false,
        AdInsertionMode: item?.AdInsertionMode ? { _id: item.AdInsertionMode, Description: item.AdInsertionMode } : {},
        AvailSuppressionMode: item?.AvailSuppression?.Mode ? { _id: item.AvailSuppression?.Mode, Description: item.AvailSuppression?.Mode } : {},
        AvailSuppressionValue: item?.AvailSuppression?.Value ?? '',
        AvailSuppressionFillPolicy: item?.AvailSuppressionFillPolicy ? { _id: item.AvailSuppressionFillPolicy, Description: item.AvailSuppressionFillPolicy } : {},
        Archive: item?.Archive ?? false,
    };

    const [dataItem, setDataItem] = useState(blankDataItem);

    const onChange = (e) => {
        if (e.target.name === 'Archive') {
            setDataItem({ ...dataItem, Archive: !dataItem.Archive });
        } else if (e.target.name === "EnableAdMarkerPassthrough") {
            setDataItem({ ...dataItem, EnableAdMarkerPassthrough: !dataItem.EnableAdMarkerPassthrough });
        }
        else {
            setDataItem({ ...dataItem, [e.target.name]: e.target.value });
        }
    };

    const isValid = () => {
        if (!dataItem.Name || !dataItem.Name == "") {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_enter_name_error_message });
            return false;
        }
        if (!dataItem.AdInsertionMode || Object.keys(dataItem.AdInsertionMode).length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_ad_insertion_mode_error_message });
            return false;
        }
        if (!dataItem.AvailSuppressionMode || Object.keys(dataItem.AvailSuppressionMode).length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_avail_suppression_mode_error_message });
            return false;
        }
        if (!dataItem.AvailSuppressionValue?.trim()) {
             ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_enter_avail_suppression_value_error_message });
            return false;
        }
        if (!dataItem.AvailSuppressionFillPolicy || Object.keys(dataItem.AvailSuppressionFillPolicy).length === 0) {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: lang.please_select_avail_suppression_fill_policy_error_message });
            return false;
        }
        return true;
    };

    const handleSubmit = async () => {
        if (!isValid()) return;

        const saveData = {
            _id: dataItem?._id,
            SID: dataItem?.SID ?? 0,
            Name: dataItem?.Name ?? "",
            AdInsertionMode: dataItem?.AdInsertionMode?.Description ?? '',
            AvailSuppression: {
                Mode: dataItem?.AvailSuppressionMode?.Description ?? '',
                Value: dataItem?.AvailSuppressionValue ?? '',
            },
            AvailSuppressionFillPolicy: dataItem?.AvailSuppressionFillPolicy?.Description ?? '',
            EnableAdMarkerPassthrough: dataItem?.EnableAdMarkerPassthrough ?? false,
            Archive: dataItem?.Archive,
            checkDuplicate: true,
            query: [["Name", "=", dataItem.Name]]
        };

        const res = await API.saveData(ENTITYNAME.DAI_AD_INSERTIONS, saveData);
        if (res.success) {
            const logData = {
                event: res.message === "updated" ? LOGEVENT.UPDATE : LOGEVENT.INSERT,
                module: MODULE.DAI_AD_INSERTION,
                data: res.data,
                message: res.message
            };
            API.SaveLogs(logData);
            utility.deleteLocalStorageItem(ENTITYNAME.DAI_AD_INSERTIONS);
            cancelEdit && cancelEdit();
            refresh && refresh();
            setData && setData(res.data);
        } else {
            ToastNotification({ type: TOAST_TYPES.ERROR, title: lang.invalid_error_message_title, message: res.message || "Failed to save data" });
        }
    };

    return (
        <BossDialog title={item?.Name ?? lang.dai_ad_insertion_dialog_header} onClose={cancelEdit} width="700px">
            <EditPageHeader title="" onSubmit={handleSubmit} onCancel={cancelEdit} showTitle={false} />
            <div className='row mt-2'>
                <div className="col-12">
                    <label htmlFor="">{lang.name_label} *</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        name="Name"
                        value={dataItem.Name}
                        onChange={onChange}
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-6">
                    <label>{lang.ad_insertionmode_label} *</label>
                    <TooltipInfoCard message="STITCHED_ONLY: Only server-side stitched ads will be used for insertion into the stream.,
                    DYNAMIC_AD_INSERTION: Ads are dynamically inserted into the stream during playback using server-side ad insertion (SSAI).,
                    CLIENT_SIDE_AD_INSERTION: Ads are inserted by the player at the client side using client-side ad insertion (CSAI)." />
                    <DropDownList
                        data={Object.keys(AD_INSERTION_MODE).map(key => ({
                            _id: AD_INSERTION_MODE[key],
                            Description: key
                        }))}
                        textField="Description"
                        dataItemKey="_id"
                        value={dataItem.AdInsertionMode}
                        onChange={onChange}
                        name="AdInsertionMode"
                    />
                </div>
                <div className="col-6">
                    <label>{lang.avail_suppression_mode_label} *</label>
                    <TooltipInfoCard message="OFF: Avail suppression is disabled. All ad avails will be included in the stream.,
                    ON: Avail suppression is enabled. Certain ad avails will be suppressed based on fill policies or other criteria." />
                    <DropDownList
                        data={Object.keys(AVAIL_SUPPRESSION_MODE).map(key => ({
                            _id: AVAIL_SUPPRESSION_MODE[key],
                            Description: key
                        }))}
                        textField="Description"
                        dataItemKey="_id"
                        value={dataItem.AvailSuppressionMode}
                        onChange={onChange}
                        name="AvailSuppressionMode"
                    />
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-6">
                    <label>{lang.avail_suppression_value_label} *</label>
                    <input
                        type="text"
                        className="form-control form-control-sm"
                        name="AvailSuppressionValue"
                        value={dataItem.AvailSuppressionValue}
                        onChange={onChange}
                    />
                </div>
                <div className="col-6">
                    <label>{lang.avail_suppression_fill_policy_label} *</label>
                    <TooltipInfoCard message="FULL_AVAIL_ONLY: Only fills the ads during the full avail periods, ignoring partial ones.,
                    PARTIAL_AVAIL: Allows filling ads even during partial avail periods when full duration isn't available.,
                    NO_SUPPRESSION: No suppression is applied; ads are filled whenever an avail is detected." />
                    <DropDownList
                        data={Object.keys(AVAIL_SUPPRESSION_FILL_POLICY).map(key => ({
                            _id: AVAIL_SUPPRESSION_FILL_POLICY[key],
                            Description: key
                        }))}
                        textField="Description"
                        dataItemKey="_id"
                        value={dataItem.AvailSuppressionFillPolicy}
                        onChange={onChange}
                        name="AvailSuppressionFillPolicy"
                    />
                </div>
            </div>
            <div className="row mt-3">
                <div className="col-6">
                    <input
                        type="checkbox"
                        name="EnableAdMarkerPassthrough"
                        checked={dataItem.EnableAdMarkerPassthrough}
                        onChange={onChange}
                    />
                    <label className="pl-2">{lang.enableAd_markerpassthrough_label}</label>
                </div>
                <div className="col-6">
                    <input
                        type="checkbox"
                        name="Archive"
                        checked={dataItem.Archive}
                        onChange={onChange}
                    />
                    <label className="pl-2">{lang.archive}</label>
                </div>
            </div>
        </BossDialog>
    );
};

export default DaiAdInsertionForm